import type { CanonicalTransaction } from "./types";

const CSV_HEADERS = [
  "transaction_id",
  "ticker",
  "company_name",
  "exchange",
  "insider_name",
  "relationships",
  "signal",
  "score",
  "confidence",
  "activity",
  "transaction_code",
  "shares",
  "price",
  "value",
  "value_quality",
  "plan_status",
  "filing_date",
  "trade_date",
  "filing_lag_days",
  "flags",
  "accession_number",
  "sec_url"
] as const;

function neutralizeSpreadsheetFormula(value: string): string {
  const leadingWhitespace = value.match(/^\s*/u)?.[0] ?? "";
  const firstMeaningfulCharacter = value.slice(leadingWhitespace.length, leadingWhitespace.length + 1);
  const hasDangerousLeadingControl = /[\t\r\n]/u.test(leadingWhitespace);
  return hasDangerousLeadingControl || /^[=+\-@]$/u.test(firstMeaningfulCharacter)
    ? `'${value}`
    : value;
}

function csvCell(value: string | number | null): string {
  if (value === null) return "";
  if (typeof value === "number") return String(value);
  const text = neutralizeSpreadsheetFormula(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function transactionValues(row: CanonicalTransaction): Array<string | number | null> {
  return [
    row.transactionId,
    row.ticker,
    row.companyName,
    row.exchange,
    row.insiderName,
    row.relationshipFlags.join("|"),
    row.signal.bucket,
    row.signal.score,
    row.signal.confidence,
    row.tradeType,
    row.transactionCode,
    row.shares,
    row.price,
    row.value,
    row.valueQuality,
    row.plan.status,
    row.filingDate,
    row.tradeDate,
    row.filingLagDays,
    row.flags.join("|"),
    row.accessionNumber,
    row.secUrl
  ];
}

export function buildTransactionsCsv(rows: readonly CanonicalTransaction[]): string {
  const records = [
    CSV_HEADERS.join(","),
    ...rows.map((row) => transactionValues(row).map(csvCell).join(","))
  ];
  return `${records.join("\r\n")}\r\n`;
}

export function downloadTransactionsCsv(
  rows: readonly CanonicalTransaction[],
  filename = "trackinsiders-results.csv"
): void {
  const blob = new Blob([buildTransactionsCsv(rows)], { type: "text/csv;charset=utf-8" });
  const href = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  link.hidden = true;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(href);
}
